import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { AIAgentMessage } from '../../models/AIAgent'
import { removeAiAgentInconsistency } from '../utils'
import { ErrorEventData, StreamingAIAgentMessage, ToolUse, ToolUseData } from '../../models/StreamingAIAgent'

interface StreamingChatState {
  messages: AIAgentMessage[]
  streamingMessageIndex: number
  streamingError: ErrorEventData | null
  toolUsesForMessages: Record<number, ToolUse[]>
}

const streamingChatInitialState: StreamingChatState = {
  messages: [],
  streamingMessageIndex: -1,
  streamingError: null,
  toolUsesForMessages: {},
}

const streamingChatSlice = createSlice({
  name: 'streamingChat',
  initialState: streamingChatInitialState,
  reducers: {
    clearChat: () => streamingChatInitialState,
    setMessages: (state, action: PayloadAction<AIAgentMessage[]>) => {
      state.messages = action.payload
    },
    addMessage: (state, action: PayloadAction<AIAgentMessage>) => {
      state.messages.push(action.payload)
    },
    beginStreamingMessage: (state) => {
      const index = state.messages.length
      state.messages.push({ type: 'agent', message: '' })
      state.streamingMessageIndex = index
    },
    updateStreamingMessage: (state, action: PayloadAction<string>) => {
      const streamingMessageIndex = state.streamingMessageIndex
      if (streamingMessageIndex < 0 || streamingMessageIndex >= state.messages.length) {
        return
      }
      const current = state.messages[streamingMessageIndex]
      state.messages[streamingMessageIndex] = {
        ...current,
        message: removeAiAgentInconsistency(action.payload),
      }
    },
    clearStreamingMessageIndex: (state) => {
      state.streamingMessageIndex = -1
    },
    addToolUseToStreamingMessage: (state, action: PayloadAction<ToolUse>) => {
      const streamingMessageIndex = state.streamingMessageIndex
      if (streamingMessageIndex < 0 || streamingMessageIndex >= state.messages.length) {
        return
      }

      const existingEntry = state.toolUsesForMessages[streamingMessageIndex]

      if (!existingEntry) {
        state.toolUsesForMessages[streamingMessageIndex] = [action.payload]
        return
      }

      existingEntry.push(action.payload)
    },
    stopToolUseInStreamingMessage: (state, action: PayloadAction<ToolUseData>) => {
      const streamingMessageIndex = state.streamingMessageIndex
      if (streamingMessageIndex < 0 || streamingMessageIndex >= state.messages.length) {
        return
      }

      const existingEntry = state.toolUsesForMessages[streamingMessageIndex]
      if (!existingEntry) {
        return
      }

      const toolIndex = existingEntry.findIndex((tool) => tool.id === action.payload.id)
      if (toolIndex === -1) {
        return
      }

      existingEntry[toolIndex] = { ...existingEntry[toolIndex], status: 'completed' as const }
    },
    addToolUseInputsToStreamingMessage: (state, action: PayloadAction<ToolUseData[]>) => {
      const streamingMessageIndex = state.streamingMessageIndex
      if (streamingMessageIndex < 0 || streamingMessageIndex >= state.messages.length) {
        return
      }

      const existingEntry = state.toolUsesForMessages[streamingMessageIndex]
      if (!existingEntry) {
        return
      }

      state.toolUsesForMessages[streamingMessageIndex] = existingEntry.map((toolUse) => {
        const toolUseData = action.payload.find((data) => data.id === toolUse.id)
        if (!toolUseData?.input) {
          return toolUse
        }
        return { ...toolUse, input: toolUseData.input }
      })
    },
    setStreamingError: (state, action: PayloadAction<ErrorEventData | null>) => {
      state.streamingError = action.payload
    },
  },
})

export const {
  clearChat,
  setMessages,
  addMessage,
  beginStreamingMessage,
  updateStreamingMessage,
  clearStreamingMessageIndex,
  stopToolUseInStreamingMessage,
  addToolUseToStreamingMessage,
  addToolUseInputsToStreamingMessage,
  setStreamingError,
} = streamingChatSlice.actions

export default streamingChatSlice.reducer

export const selectMessagesForStreaming = createSelector(
  [(state: RootState) => state.streamingChat.messages, (state: RootState) => state.streamingChat.toolUsesForMessages],
  (messages, toolUsesForMessages): StreamingAIAgentMessage[] => {
    return messages.reduce<StreamingAIAgentMessage[]>((acc, message, originalIndex) => {
      const toolUseEntry = toolUsesForMessages[originalIndex]
      acc.push({
        ...message,
        toolUses: toolUseEntry,
      })
      return acc
    }, [])
  },
)

export const selectStreamingError = (state: RootState): ErrorEventData | null => state.streamingChat.streamingError

export const selectLastUserChatMessage = createSelector([(state: RootState) => state.streamingChat.messages], (messages): AIAgentMessage | undefined => {
  return [...messages].reverse().find((message) => message.type === 'user')
})
